/**
 * "On iPhone" copy (#3126, child of the #3122 marketing epic). The App Store
 * landing page at `/app`. Sister to `members.ts` / `leaders.ts` — read those
 * headers first; the same reasons apply.
 *
 * WHY THIS IS A DATA MODULE AND NOT INLINE JSX
 * --------------------------------------------
 * `scripts/check-voice.mjs` scans `frontend/src/data/content/*.ts`, and the
 * gallery round-trip edits copy here. Copy written inline in the page is
 * outside both gates. Keeping it here means the voice gate and the editable
 * proof are real for this page from the first commit (#2954).
 *
 * THE ™ IS NOT IN THIS FILE, DELIBERATELY.
 * Core Rule 24 gives the page exactly one mark, on its most prominent use: the
 * opener. The page renders `{opener.brandLead} ORO move<sup>™</sup>
 * {opener.brandRest}` via <OroTrademark />, so the mark stays out of the copy
 * strings. There is no other "ORO move" mention on the page. The rendered ™
 * count is pinned at 1 by `AppPage.test.tsx`.
 *
 * ARRAYS ARE ARRAYS OF OBJECTS, not bare strings (same as `members.ts`): the
 * gallery `ts-object` adapter round-trips a keyed property (`chips.0.text`) but
 * cannot write back a bare `'string',` array element (no key to anchor the
 * rewrite). Keep the `{ text }` / `{ src, caption, alt }` shapes.
 *
 * VOICE (B3): NO em-dashes in shipped copy — `check-voice.mjs` enforces. The
 * opener sub and the phone caption that would have taken a dash use a full stop
 * instead. US English throughout.
 *
 * THE iOS FLOOR IS ANCHORED TO THE BUILD, NOT INVENTED HERE. `iosFloor` is the
 * default `IPHONEOS_DEPLOYMENT_TARGET` for the Expo SDK the app is built on
 * (Expo SDK 52+ defaults to 15.1; the app sets no `expo-build-properties`
 * override). The chip copy embeds it, and `AppPage.test.tsx` pins the chip to
 * `iosFloor` AND cross-checks `iosFloor` against the COMMITTED
 * `mobile/package.json` Expo SDK major (George M3) — so the check runs in
 * frontend CI and fails when the deployment target could have moved. The old
 * pbxproj cross-check was dropped: `mobile/ios/` is a gitignored prebuild
 * artifact that never exists in CI.
 *
 * AVAILABILITY sentence and the FAQ's "Is there an app?" entry (`faq.ts`) both
 * name the same territory, the word `worldwide`. The
 * owner copy pass (2026-09-11) shortened the /app sentence, so the two no longer
 * match word-for-word. `AppPage.test.tsx` imports `FAQ_DOC` and asserts that
 * shared territory phrase is present in both, so a change to either territory is
 * caught.
 */

export const APP_PAGE = {
  /** Block 1 — opener. Name the surface (iPhone) and the promise. The page's
   *  one ™ lives here, on the brand's most prominent use (Rule 24). */
  opener: {
    eyebrow: 'On iPhone',
    headline: 'Your club, in your pocket.',
    // Rendered as `{brandLead} ORO move™ {brandRest}` by the page (the ™ lives
    // in <OroTrademark />, so it stays out of these strings). This is the
    // page's single, most-prominent brand use. `brandLead` is empty by the
    // owner copy pass (2026-09-11), so the line renders as `ORO move™ for
    // iPhone.` — the page skips the empty lead rather than paint a leading
    // space (pinned by AppPage.test.tsx).
    brandLead: '',
    brandRest: 'for iPhone.',
    // B3: the owner's em-dash between the two clauses is a full stop, so
    // check-voice passes.
    sub: 'The same events, the same club room, the same check-in. Built for people on the run.',
  },

  /** Block 2 — what it is, on a phone. Three REAL product screenshots (never
   *  mock UI — owner rule: no fabricated content), then the requirement chips
   *  and the availability sentence. (The `ipad` leaf is empty by owner decision
   *  2026-09-11; the page renders nothing for an empty leaf.) */
  frames: {
    // Real captures: iPhone 17 Pro simulator, dark, default Dynamic Type,
    // Golden Hour demo club (dev data), 2026-08-19 Maestro audit run
    // (`mobile/maestro/run-audit-ios.sh`). Files under
    // `frontend/public/images/app/`. Reused from that audit capture; a refresh
    // of the three screenshots is tracked in #3462. `AppPage.test.tsx` asserts
    // each file exists on disk (a path string is not evidence).
    items: [
      {
        src: '/images/app/iphone-whats-on.png',
        caption: 'What’s on',
        alt: 'The events list, showing a club’s upcoming activities.',
      },
      {
        src: '/images/app/iphone-club-room.png',
        caption: 'The club room',
        alt: 'The club room, where members talk before the run.',
      },
      {
        src: '/images/app/iphone-on-the-day.png',
        caption: 'On the day',
        alt: 'An event with details and check-in.',
      },
    ],
  },

  /** Requirement chips + availability + iPad. NO price language of any kind
   *  ("free" is banned site-wide until ADR-0017 settles). */
  requirements: {
    // The floor lives inside the second chip verbatim; keep them in step (the
    // test pins the chip to `iosFloor`).
    chips: [
      { text: 'iPhone' },
      { text: 'iOS 15.1 or later' },
    ],
    // Anchored to the Expo SDK default (see file header). Not editable copy —
    // the gallery template references it read-only.
    iosFloor: '15.1',
    // Shares the territory phrase `worldwide` with
    // faq.ts's "Is there an app?" entry (owner 2026-09-11); the test pins that
    // shared phrase against the FAQ entry, not word-for-word sameness.
    availability: 'Available worldwide.',
    // iPad is deliberately out: `supportsTablet` is false in `mobile/app.json`
    // (#3012). Empty by owner decision 2026-09-11 — the page renders nothing
    // here. AppPage.test.tsx (George I2) asserts nothing says "iPad" while
    // supportsTablet stays false; if the build gains iPad the test fails and
    // this leaf needs a copy decision.
    ipad: '',
  },

  /** Block 3 — on anything else. Half this page's visitors are not on an
   *  iPhone; the browser path gets a real section that sends them to oro.fit in
   *  a browser. (The Android-app line was dropped in the owner copy pass
   *  2026-09-11; the lede no longer mentions Android availability.) */
  browser: {
    eyebrow: 'On anything else',
    title: 'It works in a browser too.',
    lede: 'On Android, on a laptop, on a borrowed phone: open oro.fit and sign in. Same club, same events, nothing to install.',
  },

  /** Block 4 — close. The one primary action is the App Store badge (Apple's
   *  asset, linking `/ios`, the store 302). A quiet secondary link points back
   *  to the front door for a reader who wants the argument first. */
  close: {
    title: 'Get it on your phone.',
    sub: '',
    secondary: 'Or see what it does first',
  },
} as const;
